import React from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Copy } from 'lucide-react';
import { DEFAULT_APPEARANCE, darken, fontStack } from './appearance.js';

/**
 * Header banner for the card; shape depends on the selected design.
 * @param {{ design: string, start: string, end: string, children: import('react').ReactNode }} props
 */
function Banner({ design, start, end, children }) {
  if (design === 'classic') {
    return (
      <div className="relative pt-8 pb-4 text-center flex flex-col items-center" style={{ background: start }}>
        {children}
      </div>
    );
  }
  if (design === 'professional') {
    return (
      <div>
        <div className="relative pt-8 pb-3 text-center flex flex-col items-center" style={{ background: start }}>
          {children}
        </div>
        <div className="h-4 bg-gray-300" />
      </div>
    );
  }
  if (design === 'gradient') {
    return (
      <div
        className="relative pt-8 pb-4 text-center flex flex-col items-center"
        style={{ background: `linear-gradient(180deg, ${end} 0%, ${start} 55%, #ffffff 100%)` }}
      >
        {children}
      </div>
    );
  }
  const path = design === 'modern' ? 'M0,120 L500,30 L500,120 Z' : 'M0,0 C150,90 350,-40 500,45 L500,120 L0,120 Z';
  return (
    <div
      className="relative pt-8 pb-0 text-center flex flex-col items-center justify-center overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${start} 0%, ${end} 100%)` }}
    >
      {children}
      <svg viewBox="0 0 500 120" preserveAspectRatio="none" className="w-full h-14 -mb-1 fill-white z-0">
        <path d={path} />
      </svg>
    </div>
  );
}

/**
 * Live Digital Business Card preview + share links. Reflects design, color and font immediately.
 * @param {Object} props
 * @param {Object} props.form - Digital Business Card form state.
 * @param {Object} [props.agent] - Authenticated agent (fallback contact details).
 * @param {string} props.fullBookingUrl
 * @param {string} props.fullCardUrl
 * @returns {JSX.Element}
 */
export default function BusinessCardPreview({ form, agent, fullBookingUrl, fullCardUrl }) {
  const navigate = useNavigate();
  const ap = { ...DEFAULT_APPEARANCE, ...(form.appearance || {}) };
  const start = ap.waveStart;
  const end = ap.waveEnd || darken(start);

  const displayName = `${form.firstName || 'First'} ${form.lastName || 'Last'}`.trim();
  const displayTitle = form.title || 'Title';
  const displayOrg = form.organization || 'Organization';
  const displayEmail = form.workEmail || agent?.email || 'test@example.com';
  const displayPhone = form.cellPhone || agent?.civixPhone || agent?.phone || 'Your CIVIX phone number';
  const displayTag = form.licenseInfo || form.nationalId
    ? (form.licenseInfo ? `License ID: #${form.licenseInfo}` : `National ID: #${form.nationalId}`)
    : 'NPN / License ID: #9876543';

  return (
    <div>
      <div className="text-center mb-3">
        <h4 className="text-xs font-bold text-white/70 uppercase tracking-widest">Preview</h4>
      </div>

      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-[340px] mx-auto border border-gray-200 transition-all duration-200"
        style={{ fontFamily: fontStack(ap.font), background: ap.cardBg }}
      >
        <Banner design={ap.design} start={start} end={end}>
          <div className="relative z-10 mb-2">
            {form.photoUrl ? (
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/40 shadow-xl mx-auto bg-white">
                <img src={form.photoUrl} alt={displayName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-white/40 shadow-xl mx-auto flex items-center justify-center text-white">
                <User className="w-12 h-12" />
              </div>
            )}
          </div>
        </Banner>

        <div className="px-6 pb-6 pt-2 text-left text-gray-900 space-y-4">
          <div className="flex items-start">
            <div className="w-1.5 rounded-full mr-3.5 self-stretch min-h-[44px]" style={{ backgroundColor: start }} />
            <div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight leading-snug">{displayName}</h3>
              <p className="text-xs font-medium text-gray-700">{displayTitle}</p>
              <p className="text-xs italic text-gray-500">{displayOrg}</p>
            </div>
          </div>

          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold text-purple-900 bg-purple-100/90 border border-purple-200">
            {displayTag}
          </span>

          <div className="space-y-2.5 pt-1 text-xs text-gray-700">
            {[[Mail, displayEmail], [Phone, displayPhone]].map(([Icon, value]) => (
              <div key={value} className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm"
                  style={{ backgroundColor: start }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="truncate font-medium">{value}</span>
              </div>
            ))}
          </div>

          <div className="pt-2 space-y-2">
            <a
              href={fullBookingUrl}
              target="_blank"
              rel="noreferrer"
              className="w-full py-2.5 px-4 rounded-xl text-white text-xs font-bold text-center block shadow-md transition hover:opacity-95"
              style={{ background: `linear-gradient(135deg, ${start} 0%, ${end} 100%)` }}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Book an Appointment
              </span>
            </a>
            <a
              href={`/api/user/vcard/${agent?._id || 'me'}/vcf`}
              download
              className="w-full py-2 px-4 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold text-center block transition"
            >
              Save Contact (.vcf)
            </a>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-white/[0.03] border border-white/10 rounded-xl text-center space-y-1">
        <span className="text-[11px] text-white/50 block">Direct shareable card link:</span>
        <div className="flex items-center justify-center gap-2">
          <code className="text-xs text-civix-purple font-mono truncate max-w-[240px]">{fullCardUrl}</code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(fullCardUrl);
              toast.success('Card link copied!');
            }}
            className="text-white/60 hover:text-white p-1"
            title="Copy Card URL"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/booking-page')}
          className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-white transition border border-white/10"
        >
          Next Step: Configure Booking Page →
        </button>
      </div>
    </div>
  );
}
